import React, { useEffect, useState, useRef, Component } from 'react';
import { SearchIcon, HomeIcon, UtensilsIcon, ToiletIcon, GamepadIcon } from 'lucide-react';
import Header from '../components/Header';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Fix voor Leaflet marker iconen in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Aangepaste iconen voor de faciliteiten
const createCustomIcon = (iconUrl, size = [32, 32]) => {
  return new L.Icon({
    iconUrl,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1]],
    popupAnchor: [0, -size[1]]
  });
};
const facilityIcons = {
  receptie: createCustomIcon('https://cdn-icons-png.flaticon.com/512/1786/1786049.png'),
  zwembad: createCustomIcon('https://cdn-icons-png.flaticon.com/512/2784/2784593.png'),
  restaurant: createCustomIcon('https://cdn-icons-png.flaticon.com/512/3170/3170733.png'),
  sanitair: createCustomIcon('https://cdn-icons-png.flaticon.com/512/1186/1186332.png'),
  gameroom: createCustomIcon('https://cdn-icons-png.flaticon.com/512/8002/8002123.png'),
  speeltuin: createCustomIcon('https://cdn-icons-png.flaticon.com/512/2371/2371680.png'),
  sportveld: createCustomIcon('https://cdn-icons-png.flaticon.com/512/2737/2737103.png'),
  rivier: createCustomIcon('https://cdn-icons-png.flaticon.com/512/606/606797.png'),
  tent: createCustomIcon('https://cdn-icons-png.flaticon.com/512/2106/2106996.png')
};
// Camping centrum coördinaten (fictief)
const campingCenter = [50.397, 5.927]; // Ergens in de Ardennen
// Faciliteiten data
const facilitiesData = [{
  id: 1,
  name: 'Receptie',
  category: 'service',
  position: [50.3975, 5.9275],
  icon: facilityIcons.receptie
}, {
  id: 2,
  name: 'Zwembad & Poolbar',
  category: 'recreation',
  position: [50.3965, 5.9285],
  icon: facilityIcons.zwembad
}, {
  id: 3,
  name: 'Restaurant & Bar',
  category: 'food',
  position: [50.3972, 5.9278],
  icon: facilityIcons.restaurant
}, {
  id: 4,
  name: 'Sanitairgebouw',
  category: 'service',
  position: [50.3968, 5.9265],
  icon: facilityIcons.sanitair
}, {
  id: 5,
  name: 'Gameroom',
  category: 'recreation',
  position: [50.3971, 5.9271],
  icon: facilityIcons.gameroom
}, {
  id: 6,
  name: 'Speeltuin',
  category: 'recreation',
  position: [50.3963, 5.9268],
  icon: facilityIcons.speeltuin
}, {
  id: 7,
  name: 'Sportveld',
  category: 'recreation',
  position: [50.396, 5.928],
  icon: facilityIcons.sportveld
}, {
  id: 8,
  name: 'Rivier de Amblève',
  category: 'nature',
  position: [50.3955, 5.929],
  icon: facilityIcons.rivier
}];
// Component om de kaart te herstarten als de filters veranderen
const MapController = ({
  facilities
}) => {
  const map = useMap();
  useEffect(() => {
    if (facilities.length > 0) {
      const bounds = L.latLngBounds(facilities.map(f => f.position));
      map.fitBounds(bounds, {
        padding: [50, 50]
      });
    }
  }, [facilities, map]);
  return null;
};
const Map = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOwnSpot, setShowOwnSpot] = useState(false);
  const [ownSpotNumber, setOwnSpotNumber] = useState('');
  const [ownSpotPosition, setOwnSpotPosition] = useState(null);
  const mapRef = useRef(null);
  // Filter faciliteiten op basis van categorie en zoekterm
  const filteredFacilities = facilitiesData.filter(facility => activeFilter === 'all' || facility.category === activeFilter).filter(facility => searchTerm === '' || facility.name.toLowerCase().includes(searchTerm.toLowerCase()));
  // Eigen standplaats toevoegen op de kaart
  const handleAddOwnSpot = () => {
    if (ownSpotNumber.trim() !== '') {
      // In een echte app zou dit de coördinaten opzoeken op basis van standplaatsnummer
      // Nu genereren we een willekeurige positie in de buurt van het centrum
      const randomOffset = () => (Math.random() - 0.5) * 0.003;
      setOwnSpotPosition([campingCenter[0] + randomOffset(), campingCenter[1] + randomOffset()]);
      setShowOwnSpot(true);
    }
  };
  return <div className="flex flex-col min-h-full bg-gray-50" data-id="element-496">
      <Header title="Plattegrond" showBackButton data-id="element-497" />
      <div className="p-4" data-id="element-498">
        <div className="relative mb-4" data-id="element-499">
          <input type="text" placeholder="Zoek een locatie..." className="w-full bg-white border border-gray-200 rounded-lg py-2 px-4 pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-500" />
          <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-501" />
        </div>
        <div className="flex space-x-2 overflow-x-auto pb-2 mb-4" data-id="element-502">
          <FilterButton label="Alles" active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} data-id="element-503" />
          <FilterButton label="Recreatie" active={activeFilter === 'recreation'} onClick={() => setActiveFilter('recreation')} data-id="element-504" />
          <FilterButton label="Eten & Drinken" active={activeFilter === 'food'} onClick={() => setActiveFilter('food')} data-id="element-505" />
          <FilterButton label="Service" active={activeFilter === 'service'} onClick={() => setActiveFilter('service')} data-id="element-506" />
          <FilterButton label="Natuur" active={activeFilter === 'nature'} onClick={() => setActiveFilter('nature')} data-id="element-507" />
        </div>
        <div className="bg-white rounded-lg shadow-sm p-2 mb-4 relative overflow-hidden" style={{
        height: '400px'
      }} data-id="element-508">
          <MapContainer center={campingCenter} zoom={17} style={{
          height: '100%',
          width: '100%'
        }} zoomControl={false} ref={mapRef} data-id="element-509">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-510" />
            <ZoomControl position="bottomright" data-id="element-511" />
            <MapController facilities={filteredFacilities} data-id="element-512" />
            {filteredFacilities.map(facility => <Marker key={facility.id} position={facility.position} icon={facility.icon} data-id="element-513">
                <Popup data-id="element-514">
                  <div className="text-center" data-id="element-515">
                    <h3 className="font-bold" data-id="element-516">{facility.name}</h3>
                  </div>
                </Popup>
              </Marker>)}
            {showOwnSpot && ownSpotPosition && <Marker position={ownSpotPosition} icon={facilityIcons.tent} data-id="element-517">
                <Popup data-id="element-518">
                  <div className="text-center" data-id="element-519">
                    <h3 className="font-bold" data-id="element-520">
                      Jouw Standplaats {ownSpotNumber}
                    </h3>
                  </div>
                </Popup>
              </Marker>}
          </MapContainer>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4" data-id="element-521">
          <h3 className="font-bold mb-2" data-id="element-522">Jouw Standplaats Markeren</h3>
          <div className="flex space-x-2" data-id="element-523">
            <input type="text" placeholder="Standplaatsnummer" className="flex-1 border border-gray-200 rounded-lg py-2 px-3" value={ownSpotNumber} onChange={e => setOwnSpotNumber(e.target.value)} data-id="element-524" />
            <button className="bg-[#f9b30f] text-black py-2 px-4 rounded-lg" onClick={handleAddOwnSpot} data-id="element-525">
              Markeren
            </button>
          </div>
        </div>
        <h2 className="text-lg font-bold mb-2" data-id="element-526">Faciliteiten</h2>
        <div className="space-y-2" data-id="element-527">
          {filteredFacilities.map(facility => <div key={facility.id} className="bg-white rounded-lg shadow-sm p-3 flex items-center" data-id="element-528">
              <div className="text-[#f9b30f] mr-3" data-id="element-529">
                <img src={facility.icon.options.iconUrl} alt={facility.name} className="w-6 h-6" data-id="element-530" />
              </div>
              <span data-id="element-531">{facility.name}</span>
            </div>)}
        </div>
        <div className="mt-6 bg-white rounded-lg shadow-sm p-4" data-id="element-532">
          <h3 className="font-bold mb-2" data-id="element-533">Ligging</h3>
          <p className="text-sm text-gray-600" data-id="element-534">
            Camping Olympia ligt in het Amblèvedal in België, direct aan de
            rivier de Amblève. De camping is volledig vernieuwd in het voorjaar
            van 2022 en biedt een perfecte uitvalsbasis voor natuurliefhebbers
            en avonturiers.
          </p>
        </div>
      </div>
    </div>;
};
const FilterButton = ({
  label,
  active,
  onClick
}) => {
  return <button className={`py-1 px-3 rounded-full text-sm whitespace-nowrap ${active ? 'bg-[#f9b30f] text-black' : 'bg-white text-gray-600 border border-gray-200'}`} onClick={onClick} data-id="element-535">
      {label}
    </button>;
};
export default Map;